import { AxiosResponse } from "axios";
import { request, parseLocationData } from "@utils";
import {
  Locations,
  LocationRead,
  LocationPayload,
  LocationSearchResponse,
} from "@types";

export const getAllLocations = async (): Promise<AxiosResponse<LocationRead[]>> => {
  const req = request();
  const response = await req.get("/api/v1/locations");
  // Parse each location to convert officeHours to weekSchedule
  if (response.data && Array.isArray(response.data)) {
    response.data = response.data.map(parseLocationData);
  }
  return response;
};

export const createLocation = async (
  data: Locations,
): Promise<LocationRead> => {
  const res = await request().post("/api/v1/locations", data);
  return res.data;
};

export const updateLocation = async (
  id: string,
  data: LocationPayload,
): Promise<LocationRead> => {
  const res = await request().put(`/api/v1/locations/${id}`, data);
  return parseLocationData(res.data);
};

export const updateLocationHours = async (
  id: string,
  hours: {
    officeHours?: any;
    gateAccessHours?: any;
  },
): Promise<LocationRead> => {
  const res = await request().put(`/api/v1/locations/${id}/hours`, hours);

  // The API returns { success: boolean, message: string, location: LocationRead }
  // We need to return just the location and parse it
  let location = res.data;
  if (res.data && res.data.location) {
    location = res.data.location;
  }
  // Parse to convert officeHours to weekSchedule
  return parseLocationData(location);
};

export const searchLocationsByZip = async (
  zip: string,
): Promise<LocationRead[] | null> => {
  try {
    const req = request();
    const response: AxiosResponse<LocationSearchResponse> = await req.get(
      `/api/v1/locations?zip=${zip}`,
    );
    return response.data.locations;
  } catch (error) {
    console.error("Error searching locations by zip:", error);
    return null;
  }
};

export const uploadLocationImage = async (
  id: string,
  file: File,
): Promise<LocationRead> => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await request().post(`/api/v1/upload/location/${id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return res.data;
};

export const uploadLocationPhoto = async (
  id: string,
  file: File,
): Promise<{ photo: LocationPhoto; photos: LocationPhoto[] }> => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await request().post(`/api/v1/upload/location/${id}/photos`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return res.data;
};

export const deleteLocationPhoto = async (
  id: string,
  photoUrl: string,
): Promise<{ photos: LocationPhoto[] }> => {
  const res = await request().delete(`/api/v1/upload/location/${id}/photos`, {
    data: { url: photoUrl },
  });

  return res.data;
};

export const setLocationMainPhoto = async (
  id: string,
  photoUrl: string,
): Promise<{ photos: LocationPhoto[] }> => {
  const res = await request().put(`/api/v1/upload/location/${id}/photos/main`, {
    url: photoUrl
  });

  return res.data;
};

export interface LocationPhoto {
  url: string;
  isMain: boolean;
}
